import { CollectionTools } from "../../../../components/collectionTools";
import { useState, useEffect, useRef } from "react";
// Redux
import { useDispatch, useSelector } from "react-redux";
import {
  setAlert,
  setLoading,
  updateDatatableCheckColumn,
  updateDatatableRemovedRows,
  updateDatatableRequestQuery,
  updateDatatableSelectedRows,
  updateFloatPanel,
} from "../../../../reducers/userConfigSlice";
// Components
import { FiAlertCircle, FiInbox, FiRefreshCw } from "react-icons/fi";
import { LuPlus as GoPlus } from "react-icons/lu";
import { Button, translateMessage } from "@delibrandev/utils";
//import { Datatable } from "@delibrandev/utils";
// API
import { addIdKeyIfMissing } from "@delibrandev/utils/src/function.js";
// Styles
import "./style.css";
import { Datatable } from "@delibrandev/utils";

export const TableLayout = ({
  title = "",
  addText = "",
  headerAction = null,
  emptyTitle = "Todavía no hay registros",
  emptyDescription = "Cuando añadas el primero, aparecerá aquí.",
  edition = <></>,
  get = () => null,
  remove = () => null,
  sections = [],
  customHeaders = {},
  customHeadersStyle = {},
  customData = {},

  identificator = "default",
  idKey = null,
  showLayoutToggle = true,
  renderCards = null,
  renderSummary = null,
  renderContent = null,
  collection = null,
}) => {
  const renderTable = (rows) => (
    <Datatable
      data={rows}
      customHeaders={customHeaders}
      customHeadersStyle={customHeadersStyle}
      customData={
        collection
          ? Object.fromEntries(
              Object.entries(customHeaders).map(([key, label]) => [
                key,
                (value, row) => (
                  <div className="collectionCell">
                    <span className="collectionMobileLabel">{label}</span>
                    <div>
                      {customData[key]
                        ? customData[key](value, row)
                        : value == null
                          ? "—"
                          : typeof value === "object"
                            ? JSON.stringify(value)
                            : String(value)}
                    </div>
                  </div>
                ),
              ]),
            )
          : customData
      }
      sections={sections}
      rowCallback={handleFloatPanel}
      deleteCallback={queryRemove}
      identificator={identificator}
      checkColumn={checkColumn}
      checkedRows={checkedRows}
      handleCheckColumn={handleCheckColumn}
      handleSelectedRows={handleSelectedRows}
    />
  );
  const [tableToolsOpen, setTableToolsOpen] = useState(false);
  const [layout, setLayout] = useState(renderCards ? "cards" : "table");
  const dispatch = useDispatch();
  const panelVisible = useSelector(
    (state) => state.aplicationConfig.userConfig.floatPanel.visible,
  );
  const requestQuery = useSelector(
    (state) => state.aplicationConfig.userConfig.datatable.requestQuery,
  );
  const checkColumn = useSelector(
    (state) =>
      state.aplicationConfig.userConfig.datatable.tables[identificator]
        ?.checkColumn,
  );
  const checkedRows = useSelector(
    (state) =>
      state.aplicationConfig.userConfig.datatable.tables[identificator]
        ?.selectedRows,
  );
  const [data, setData] = useState([]);
  const [readSucceeded, setReadSucceeded] = useState(false);
  const [readError, setReadError] = useState(false);
  const [readPending, setReadPending] = useState(false);
  const readInFlight = useRef(false);
  const selectedQuery = useRef(get);
  const queuedQuery = useRef(false);
  const refreshVersion = useSelector(
    (state) => state.aplicationConfig.userConfig.datatable.refreshVersion || 0,
  );
  const lastRefreshVersion = useRef(refreshVersion);

  const handleCheckColumn = () => {
    dispatch(updateDatatableCheckColumn({ table: identificator }));
  };

  const handleSelectedRows = (updatedSelectedRows = null) => {
    dispatch(
      updateDatatableSelectedRows({
        selectedRows: updatedSelectedRows,
        table: identificator,
      }),
    );
  };

  /*********** API REQUESTS ************/
  const queryGet = async () => {
    if (readInFlight.current) return;
    readInFlight.current = true;
    setReadPending(true);
    setReadError(false);
    dispatch(setLoading(true));
    try {
      const response = await selectedQuery.current();
      const rows = response?.data?.orders ?? response?.data;
      if (!response || response.error || !Array.isArray(rows)) {
        setReadError(true);
        return;
      }
      setData(addIdKeyIfMissing(rows, idKey));
      setReadSucceeded(true);
    } catch {
      setReadError(true);
    } finally {
      readInFlight.current = false;
      setReadPending(false);
      dispatch(setLoading(false));
      if (queuedQuery.current) {
        queuedQuery.current = false;
        Promise.resolve().then(queryGet);
      }
    }
  };

  const queryRemove = async (selectedRows) => {
    dispatch(setLoading(true));
    const response = await remove(selectedRows);
    if (response && !response.error) {
      dispatch(setAlert(translateMessage(response?.message || "Bad request")));
      dispatch(
        updateDatatableRemovedRows({
          removedRows: selectedRows,
          table: identificator,
        }),
      );
      console.log("Operación exitosa", response);
      queryGet();
    } else {
      dispatch(setAlert(translateMessage(response?.message || "Bad request")));
      console.log("Error en la operación");
    }
    dispatch(setLoading(false));
  };

  useEffect(() => {
    const refreshChanged = lastRefreshVersion.current !== refreshVersion;
    lastRefreshVersion.current = refreshVersion;
    if (!panelVisible || refreshChanged) queryGet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panelVisible, refreshVersion]);

  const customRequest = async (query) => {
    selectedQuery.current = query;
    if (readInFlight.current) {
      queuedQuery.current = true;
      return;
    }
    await queryGet();
  };

  //Listener, escucha si hay un cambio de pestaña en la tabla para realizar la query que le manda el padre.
  useEffect(() => {
    if (requestQuery && requestQuery.request) {
      customRequest(requestQuery.query);
      const payload = {
        query: null,
        request: false,
      };
      dispatch(updateDatatableRequestQuery(payload));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestQuery]);

  // =========== Edition Configuration ============
  const handleFloatPanel = (data = null, isNew = false) => {
    if (!isNew) {
      dispatch(
        updateFloatPanel({
          visible: true,
          queryId: data.id,
          isEmpty: false,
          data: data,
        }),
      );
    } else {
      dispatch(
        updateFloatPanel({
          visible: true,
          queryId: null,
          isEmpty: true,
          data: null,
        }),
      );
    }
  };

  return (
    <>
      <div className="topContainer" hidden={!title && !addText}>
        <div className="pageHeading">
          <h1 className="bodyTitle">{title}</h1>
          <p>
            {{
              Productos:
                "Organiza tu carta, sus precios y cómo la ven tus clientes.",
              Categorías:
                "Orden en el que se agrupan los productos dentro de cada catálogo.",
              Catálogos: "Agrupaciones de productos que se publican juntas en cada canal.",
              Descuentos:
                "Códigos y promociones aplicables en tienda y en el TPV.",
              Clientes: "Fichas de clientes con su contacto y su histórico de compra.",
              Atributos: "Variantes y atributos reutilizables que se aplican a los productos configurables.",
              "Grupos de atributos":
                "Organiza los extras y opciones de tus productos.",
              Pedidos: "Consulta el estado y los detalles de cada pedido.",
              "Canal de venta":
                "Canales desde los que entran pedidos y su estado operativo.",
            }[title] ||
              "Toda la información de esta sección, en un mismo lugar."}
          </p>
        </div>
        <div className="actionsDashboard">
          {renderCards && showLayoutToggle && (
            <div className="adminViewSwitch" aria-label="Vista del listado">
              <button
                type="button"
                aria-pressed={layout === "cards"}
                onClick={() => setLayout("cards")}
              >
                Tarjetas
              </button>
              <button
                type="button"
                aria-pressed={layout === "table"}
                onClick={() => setLayout("table")}
              >
                Tabla
              </button>
            </div>
          )}
          {/*} <Button text={"Exportar"} icon={<PiExport />} />*/}
          {headerAction}
          {addText !== "" && (
            <Button
              text={addText}
              icon={<GoPlus />}
              customClass={"customButtonColor2"}
              action={() => handleFloatPanel(null, true)}
            />
          )}
        </div>
      </div>
      {renderSummary && readSucceeded && !readError && renderSummary(data)}
      {readPending && (
        <p className="tableReadPending" role="status">
          Cargando listado…
        </p>
      )}
      {readError && (
        <div className="tableReadState tableReadError">
          <FiAlertCircle aria-hidden="true" className="tableReadIcon" />
          <h2>No se ha podido cargar el listado</h2>
          <p role="alert">
            Ha ocurrido un problema al cargar los datos. Vuelve a intentarlo.
          </p>
          <button className="tableRetryButton" type="button" onClick={queryGet}>
            <FiRefreshCw aria-hidden="true" /> Reintentar
          </button>
        </div>
      )}
      {!readError &&
        readSucceeded &&
        (renderContent ? (
          renderContent(data, handleFloatPanel)
        ) : data.length === 0 ? (
          <div className="tableReadState" role="status">
            <FiInbox aria-hidden="true" className="tableReadIcon" />
            <h2>{emptyTitle}</h2>
            <p>{emptyDescription}</p>
          </div>
        ) : (
          <div
            className={`datatableContainer adminTableFrame ${tableToolsOpen ? "showTableTools" : ""}`}
          >
            {title === "Productos" && !collection && (
              <button
                type="button"
                className="adminTableToolsToggle"
                aria-expanded={tableToolsOpen}
                onClick={() => setTableToolsOpen((previous) => !previous)}
              >
                {tableToolsOpen
                  ? "Ocultar filtros y ordenación"
                  : "Filtros y ordenación"}
              </button>
            )}
            {renderCards && layout === "cards" ? (
              renderCards(data, handleFloatPanel)
            ) : collection ? (
              <CollectionTools rows={data} config={collection}>
                {renderTable}
              </CollectionTools>
            ) : (
              renderTable(data)
            )}
          </div>
        ))}
      {edition}
    </>
  );
};
